import { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  convertToPixelCrop,
  convertToPercentCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { canvasPreview } from "../../utils/canvasPreview";
import { useDebounceEffect } from "../../hooks/useDebounceEffect";
import { Download } from "lucide-react";

/**
 * Standalone modal: upload image, crop/resize with same options as Create Main Company logo,
 * then download the result. User can then upload the downloaded file manually in Create Main Company.
 */
export default function LogoResizerModal({ onClose, showToast }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [crop, setCrop] = useState(undefined);
  const [completedCrop, setCompletedCrop] = useState(undefined);
  const [scale, setScale] = useState(1);
  const [aspect, setAspect] = useState(4);
  const [imageSize, setImageSize] = useState(null);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const downloadAnchorRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (aspect === "free" || !imageSize || !file) return;
    const { naturalWidth, naturalHeight } = imageSize;
    const percentCrop = centerCrop(
      makeAspectCrop({ unit: "%", width: 90 }, aspect, naturalWidth, naturalHeight),
      naturalWidth,
      naturalHeight
    );
    setCrop(percentCrop);
    setCompletedCrop(convertToPixelCrop(percentCrop, naturalWidth, naturalHeight));
  }, [aspect, file, imageSize]);

  useDebounceEffect(
    () => {
      if (!completedCrop?.width || !completedCrop?.height || !imgRef.current || !previewCanvasRef.current) return;
      canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, 0);
    },
    50,
    [completedCrop, scale]
  );

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreviewUrl((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
    setFile(f);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setScale(1);
    setImageSize(null);
  };

  const onImageLoad = useCallback((e) => {
    const { naturalWidth, naturalHeight, width, height } = e.currentTarget;
    setImageSize({ naturalWidth, naturalHeight, width, height });
    if (aspect === "free") {
      const percentCrop = { unit: "%", x: 25, y: 25, width: 50, height: 50 };
      setCrop(percentCrop);
      setCompletedCrop(convertToPixelCrop(percentCrop, width, height));
    } else {
      const percentCrop = centerCrop(
        makeAspectCrop({ unit: "%", width: 90 }, aspect, naturalWidth, naturalHeight),
        naturalWidth,
        naturalHeight
      );
      setCrop(percentCrop);
      setCompletedCrop(convertToPixelCrop(percentCrop, naturalWidth, naturalHeight));
    }
  }, [aspect]);

  const getCroppedBlob = useCallback(
    (image, previewCanvas, cropVal, scaleVal, fileType) =>
      new Promise((resolve, reject) => {
        if (!image || !previewCanvas || !cropVal?.width || !cropVal?.height) {
          reject(new Error("Crop or image missing"));
          return;
        }
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const offscreen = document.createElement("canvas");
        offscreen.width = Math.round(cropVal.width * scaleX);
        offscreen.height = Math.round(cropVal.height * scaleY);
        const ctx = offscreen.getContext("2d");
        if (!ctx) {
          reject(new Error("No 2d context"));
          return;
        }
        ctx.drawImage(
          previewCanvas,
          0,
          0,
          previewCanvas.width,
          previewCanvas.height,
          0,
          0,
          offscreen.width,
          offscreen.height
        );
        offscreen.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
          fileType?.startsWith("image/") ? fileType : "image/png",
          0.95
        );
      }),
    []
  );

  const handleDownload = useCallback(async () => {
    if (!completedCrop || !imgRef.current || !file || !previewCanvasRef.current) return;
    try {
      const blob = await getCroppedBlob(
        imgRef.current,
        previewCanvasRef.current,
        completedCrop,
        scale,
        file.type
      );
      const url = URL.createObjectURL(blob);
      const a = downloadAnchorRef.current;
      if (a) {
        a.href = url;
        a.download = file.name ? file.name.replace(/\.[^.]+$/, "_resized.png") : "logo_resized.png";
        a.click();
      }
      URL.revokeObjectURL(url);
      if (typeof showToast === "function") showToast("Image downloaded. Upload it in Create Main Company → Logo.", "success");
    } catch (err) {
      console.error("Download failed", err);
      if (typeof showToast === "function") showToast("Download failed.", "error");
    }
  }, [completedCrop, scale, file, getCroppedBlob, showToast]);

  const handleDownloadOriginal = useCallback(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const a = downloadAnchorRef.current;
    if (a) {
      a.href = url;
      a.download = file.name || "logo_original.png";
      a.click();
    }
    URL.revokeObjectURL(url);
    if (typeof showToast === "function") showToast("Original image downloaded.", "success");
  }, [file, showToast]);

  const handleManualDimensions = useCallback((naturalW, naturalH) => {
    if (!imgRef.current || !imageSize || !completedCrop) return;
    const img = imgRef.current;
    const scaleX = imageSize.naturalWidth / imageSize.width;
    const scaleY = imageSize.naturalHeight / imageSize.height;
    let wDisp = Math.max(20, Math.min(naturalW / scaleX, img.width - completedCrop.x));
    let hDisp = Math.max(20, Math.min(naturalH / scaleY, img.height - completedCrop.y));
    const newPixelCrop = {
      unit: "px",
      x: completedCrop.x,
      y: completedCrop.y,
      width: wDisp,
      height: hDisp,
    };
    setCompletedCrop(newPixelCrop);
    setCrop(convertToPercentCrop(newPixelCrop, img.width, img.height));
  }, [imageSize, completedCrop]);

  const handleClose = useCallback(() => {
    if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setImageSize(null);
    onClose();
  }, [onClose, previewUrl]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Upload an image, crop and resize it, then download. Use the downloaded file as the logo when creating a main company.
      </p>

      {/* User instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
        <h3 className="font-semibold text-blue-900 mb-2">How to use Logo Resizer</h3>
        <ol className="list-decimal list-inside space-y-1.5 text-blue-800">
          <li><strong>Choose image</strong> — Click &quot;Choose image&quot; and select your logo file (e.g. PNG, JPG).</li>
          <li><strong>Aspect ratio</strong> — Pick a fixed ratio (2∶1, 4∶1, etc.) for a consistent logo shape, or &quot;Free (manual)&quot; to set width and height independently.</li>
          <li><strong>Crop area</strong> — Drag the corners or edges of the crop box to select the part of the image you want. Move the box by dragging inside it.</li>
          <li><strong>Scale</strong> — Use the scale slider to zoom the image inside the crop (e.g. 1.5 = zoom in). The preview updates as you move the slider.</li>
          <li><strong>Manual width/height</strong> — If you chose &quot;Free (manual)&quot;, type exact output dimensions (in pixels) in the Width and Height boxes.</li>
          <li><strong>Preview</strong> — Check the small preview under the slider to see the final result before downloading.</li>
          <li><strong>Download</strong> — Click &quot;Download resized&quot; to save the cropped image, or &quot;Download original&quot; to save the file without any changes.</li>
          <li><strong>Use as company logo</strong> — Go to Create Main Company, open the Company Logo field, and upload the file you just downloaded.</li>
        </ol>
      </div>

      {!file ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Choose image</label>
          <input
            type="file"
            accept="image/*"
            onChange={onFileChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>
      ) : (
        <div className="space-y-3 border rounded-lg p-4 bg-gray-50">
          {imageSize && (
            <p className="text-xs text-gray-600">
              Original: <strong>{imageSize.naturalWidth}</strong> × <strong>{imageSize.naturalHeight}</strong> px
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs font-medium text-gray-700">Aspect ratio:</label>
            <select
              value={aspect}
              onChange={(e) => setAspect(e.target.value === "free" ? "free" : Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="free">Free (manual)</option>
              <option value={2}>2∶1</option>
              <option value={3}>3∶1</option>
              <option value={4}>4∶1</option>
              <option value={5}>5∶1</option>
              <option value={6}>6∶1</option>
              <option value={8}>8∶1</option>
            </select>
          </div>
          <div className="max-h-[280px] overflow-auto rounded border bg-white">
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
              aspect={aspect === "free" ? undefined : aspect}
              minWidth={20}
              minHeight={20}
            >
              <img
                ref={imgRef}
                src={previewUrl}
                alt="Crop"
                onLoad={onImageLoad}
                style={{
                  maxHeight: 260,
                  width: "100%",
                  objectFit: "contain",
                  transform: `scale(${scale})`,
                  willChange: "transform",
                }}
              />
            </ReactCrop>
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">Scale: {scale.toFixed(2)}</label>
            <input
              type="range"
              min={0.25}
              max={2}
              step={0.05}
              value={scale}
              onChange={(e) => setScale(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-200 accent-blue-600"
            />
          </div>
          {completedCrop && imageSize && (
            <>
              <p className="text-xs text-gray-600">
                Output: <strong>{Math.round(completedCrop.width * (imageSize.naturalWidth / imageSize.width))}</strong> ×{" "}
                <strong>{Math.round(completedCrop.height * (imageSize.naturalHeight / imageSize.height))}</strong> px
              </p>
              {aspect === "free" && (
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700">Width (px):</span>
                    <input
                      type="number"
                      min={20}
                      max={imageSize.naturalWidth || 2000}
                      value={Math.round(completedCrop.width * (imageSize.naturalWidth / imageSize.width))}
                      onChange={(e) => {
                        const w = Number(e.target.value);
                        const h = Math.round(completedCrop.height * (imageSize.naturalHeight / imageSize.height));
                        if (!Number.isNaN(w) && w >= 20) handleManualDimensions(w, h);
                      }}
                      className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </label>
                  <label className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700">Height (px):</span>
                    <input
                      type="number"
                      min={20}
                      max={imageSize.naturalHeight || 2000}
                      value={Math.round(completedCrop.height * (imageSize.naturalHeight / imageSize.height))}
                      onChange={(e) => {
                        const h = Number(e.target.value);
                        const w = Math.round(completedCrop.width * (imageSize.naturalWidth / imageSize.width));
                        if (!Number.isNaN(h) && h >= 20) handleManualDimensions(w, h);
                      }}
                      className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </label>
                </div>
              )}
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-700">Preview:</span>
                <canvas
                  ref={previewCanvasRef}
                  className="border border-gray-300 rounded bg-white"
                  style={{
                    width: completedCrop.width,
                    height: completedCrop.height,
                    objectFit: "contain",
                    maxWidth: "200px",
                    maxHeight: "80px",
                  }}
                />
              </div>
            </>
          )}
          <div className="flex gap-2 flex-wrap pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-3 py-1.5 text-sm bg-gray-300 rounded hover:bg-gray-400"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleDownloadOriginal}
              className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Download original
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={!completedCrop}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            >
              <Download className="w-4 h-4" />
              Download resized
            </button>
          </div>
        </div>
      )}

      <a
        ref={downloadAnchorRef}
        href="#hidden"
        download
        className="hidden"
        aria-hidden
      >
        download
      </a>
    </div>
  );
}
